import { useEffect, useState } from 'react';
import { Size, t, type AttributeDefinition, type PagedRecord, type SuggestionRow } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Checkbox, DatePicker, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import styles from './RecordPage.module.css';
import { useCompanySearch, usePersonSearch } from '../settings/api';
import { useAddNew } from './AddNewHost';
import { PAGES_PATH, apiFetch } from './api';
import { reportCaughtInBrowser } from '../../shell/systemErrors';

/**
 * The Custom fields tile's Edit (piece 13, decision 0233): every active
 * definition whose `appliesTo` names the record type, each written through its
 * own value column and validated by its kind on the server. A required
 * definition blocks Save on this page and nowhere else. 🔨 The changed fields
 * go as one batch (decision 0355): the server validates every one first and
 * writes all or none, and a refusal comes back per field and is drawn under
 * the field it names, with the draft kept.
 */
export interface CustomFieldsModalProps {
  open: boolean;
  recordType: PagedRecord;
  recordId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
}

interface TileAnswer {
  shown: boolean;
  rows: { definition: AttributeDefinition; value: { textValue?: string | null; numericValue?: number | null; dateValue?: string | null; yesNoValue?: boolean | null; referenceRecordId?: string | null } | null; referenceName?: string | null }[];
}

/** The kind of record a Reference definition names; a definition that names none points at a company, as the phone's form reads it. */
function referenceKindOf(definition: Pick<AttributeDefinition, 'referenceRecordType'>): 'CompanyRecord' | 'PersonRecord' {
  return definition.referenceRecordType === 'PersonRecord' ? 'PersonRecord' : 'CompanyRecord';
}

type Draft = Record<string, string | boolean>;

function initialOf(rows: TileAnswer['rows']): Draft {
  const draft: Draft = {};
  for (const { definition, value } of rows) {
    if (!value) continue;
    if (definition.kind === 'YesNo') draft[definition.id] = value.yesNoValue ?? false;
    else if (definition.kind === 'Number' || definition.kind === 'Measured') draft[definition.id] = value.numericValue === null || value.numericValue === undefined ? '' : String(value.numericValue);
    // The draft of a date field holds an ISO day for the date input, not display text (T663).
    else if (definition.kind === 'Date') draft[definition.id] = value.dateValue ? String(value.dateValue).slice(0, 10) : '';
    else if (definition.kind === 'Reference') draft[definition.id] = value.referenceRecordId ?? '';
    else draft[definition.id] = value.textValue ?? '';
  }
  return draft;
}

function bodyOf(definition: AttributeDefinition, value: string | boolean | undefined) {
  switch (definition.kind) {
    case 'YesNo':
      return { yesNoValue: value === true };
    case 'Number':
    case 'Measured':
      return { numericValue: value === '' || value === undefined ? null : Number(value) };
    case 'Date':
      return { dateValue: value ? String(value) : null };
    case 'Reference':
      // 🔒 Row T3801: the id goes with the kind it names, or the server refuses it for not recording which type it points at.
      return value ? { referenceRecordType: referenceKindOf(definition), referenceRecordId: String(value) } : { referenceRecordId: null };
    default:
      return { textValue: value === undefined ? null : String(value) };
  }
}

interface BatchProblem {
  attributeTypeId: string;
  attributeName: string | null;
  problem: string;
}

/** The batch write, with the server's per-field refusal read off the body rather than flattened to one line. */
async function saveBatch(url: string, values: unknown[]): Promise<{ ok: true } | { ok: false; message: string; problems: BatchProblem[] }> {
  try {
    const response = await apiFetch(url, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ values }) });
    if (response.ok) return { ok: true };
    const payload = (await response.json().catch(/* silent: a body that is not JSON reads as none, and the status check beside it answers the failure. */ () => null)) as { message?: string | string[]; problems?: BatchProblem[] } | null;
    const message = Array.isArray(payload?.message) ? payload.message.join('; ') : payload?.message;
    return { ok: false, message: message ?? `the server refused the write (${response.status})`, problems: Array.isArray(payload?.problems) ? payload.problems : [] };
  } catch {
    // silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server.
    return { ok: false, message: 'the server could not be reached', problems: [] };
  }
}

export function CustomFieldsModal({ open, recordType, recordId, apiBaseUrl = '', onCancel, onSaved, openerId = null }: CustomFieldsModalProps) {
  const [rows, setRows] = useState<TileAnswer['rows']>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  /** The refused batch's problems, one per definition, drawn under the field each names. */
  const [problems, setProblems] = useState<Record<string, string>>({});
  /** The name shown beside a held or picked Reference id, keyed by the definition (piece 13: a field that stores an id shows its name). */
  const [names, setNames] = useState<Record<string, string>>({});
  const searchCompanies = useCompanySearch(apiBaseUrl);
  const searchPeople = usePersonSearch(apiBaseUrl);
  const addNew = useAddNew(apiBaseUrl);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${PAGES_PATH}/custom-fields/${recordType}/${recordId}`, { signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<TileAnswer>) : { shown: false, rows: [] }))
      .then((body) => {
        if (controller.signal.aborted) return;
        setRows(body.rows);
        setDraft(initialOf(body.rows));
        setNames(Object.fromEntries(body.rows.filter((row) => row.referenceName).map((row) => [row.definition.id, row.referenceName!])));
      })
      .catch((failed: unknown) => {
        reportCaughtInBrowser('records.customFields.read', failed);
      });
    return () => controller.abort();
  }, [open, recordType, recordId, apiBaseUrl]);

  const set = (id: string, value: string | boolean) => {
    setDraft((held) => ({ ...held, [id]: value }));
    setTouched((held) => new Set(held).add(id));
  };

  const pickReference = (definition: AttributeDefinition, row: SuggestionRow) => {
    set(definition.id, row.id);
    setNames((held) => ({ ...held, [definition.id]: row.line1 }));
  };

  const save = async () => {
    setError(null);
    setProblems({});
    const values = rows.filter(({ definition }) => touched.has(definition.id)).map(({ definition }) => ({ attributeTypeId: definition.id, ...bodyOf(definition, draft[definition.id]) }));
    const answer = await saveBatch(`${apiBaseUrl}${PAGES_PATH}/custom-fields/${recordType}/${recordId}`, values);
    if (!answer.ok) {
      setError(answer.problems.length > 0 ? t('customFieldsModal.refused') : answer.message);
      setProblems(Object.fromEntries(answer.problems.map((p) => [p.attributeTypeId, p.problem])));
      return;
    }
    onSaved();
    onCancel();
  };

  return (
    <Modal
      open={open}
      title={t('customFieldsModal.title')}
      subtitle={t('customFieldsModal.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="custom-fields"
      actions={[
        { kind: 'primary', label: t('customFieldsModal.save'), onAct: () => void save(), disabled: touched.size === 0 },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-custom-fields-modal>
        {error && <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        {rows.map(({ definition }) => {
          const value = draft[definition.id];
          const problem = problems[definition.id] ?? null;
          const field = (control: JSX.Element) => (
            <div key={definition.id} data-custom-field={definition.id} data-custom-field-problem={problem ? 'true' : 'false'}>
              {control}
              {problem ? <p className={styles.problem} role="alert">{problem}</p> : null}
            </div>
          );
          if (definition.kind === 'YesNo') {
            return field(<Checkbox label={definition.attributeName} checked={value === true} onChange={(checked) => set(definition.id, checked)} />);
          }
          if (definition.kind === 'Enumerated' && definition.values) {
            return field(
              <Select size={Size.Medium}
                label={definition.attributeName}
                required={definition.isRequired ?? false}
                value={String(value ?? '')}
                onChange={(next) => set(definition.id, next)}
                placeholder={t('customFieldsModal.none')}
                options={definition.values.map((option) => ({ value: option.value, label: option.value }))}
              />,
            );
          }
          if (definition.kind === 'Date') {
            return field(<DatePicker size={Size.Small} label={definition.attributeName} value={String(value ?? '')} onChange={(next) => set(definition.id, next)} mode="date" required={definition.isRequired ?? false} name={`custom-${definition.id}`} />);
          }
          if (definition.kind === 'Number' || definition.kind === 'Measured') {
            const held = typeof value === 'string' && value.trim() !== '' ? Number(value) : null;
            return field(
              <NumericInput size={Size.Small}
                label={definition.attributeName}
                kind="number"
                value={held !== null && Number.isFinite(held) ? held : null}
                onChange={(next) => set(definition.id, next === null ? '' : String(next))}
                required={definition.isRequired ?? false}
                name={`custom-${definition.id}`}
              />,
            );
          }
          if (definition.kind === 'Reference') {
            const kind = referenceKindOf(definition);
            return field(
              <Dropdown
                size={Size.Medium}
                label={definition.attributeName}
                entityLabel={t(kind === 'PersonRecord' ? 'customFieldsModal.personEntity' : 'customFieldsModal.companyEntity')}
                search={kind === 'PersonRecord' ? searchPeople : searchCompanies}
                value={names[definition.id] ?? null}
                required={definition.isRequired ?? false}
                onSelect={(picked: SuggestionRow) => pickReference(definition, picked)}
                onAddNew={() => addNew.open({ kind: 'record', recordType: kind }, (picked: SuggestionRow) => pickReference(definition, picked))}
              />,
            );
          }
          return field(
            <TextInput size={Size.Medium}
              label={definition.attributeName}
              value={String(value ?? '')}
              onChange={(next) => set(definition.id, next)}
              required={definition.isRequired ?? false}
            />,
          );
        })}
        {addNew.host}
      </div>
    </Modal>
  );
}
